// RedemptionCard.jsx
// REFACTORED — merchant-side redemption approval with swipe gestures
// Swipe right = approve, left = reject
// After approval: shows "Confirm Redeemed" final step

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";
import SwipeCard from "./SwipeCard";

const REMARK_SUGGESTIONS = {
    approve: [
        "10% discount applied",
        "20% discount applied",
        "50% discount applied",
        "BOGO — Buy 1 Get 1 Free",
        "Free gift item",
        "Birthday cake redemption",
        "Free dessert / beverage",
        "Flat ₹100 off on bill",
        "Loyalty voucher applied",
        "Combo offer redeemed",
    ],
    reject: [
        "Insufficient points balance",
        "Offer not valid at this outlet",
        "Customer details mismatch",
        "Duplicate redemption request",
        "Promotion expired",
    ],
};

const RedemptionCard = ({ redemption, onRefresh }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;

    const [loading,  setLoading]  = useState(false);
    const [message,  setMessage]  = useState(null);
    const [done,     setDone]     = useState(false);
    const [localStatus, setLocalStatus] = useState(redemption.status);

    const [remarks, setRemarks] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);

    if (done) return null;

    // ── Approve ─────────────────────────────────────────────────────
    // const handleApprove = async () => {
    //     try {
    //         setLoading(true);
    //         const res = await fetchData({
    //             url: "/pd/streaks/redemption-approve-redeem",
    //             method: "post",
    //             data: {
    //                 redemptionId: redemption._id,
    //                 approvedBy:   authState?.user?._id,
    //                 approvedPoints: redemption.requestedPoints,
    //                 approvalRemarks: remarks.trim() || "Approved and redeemed by merchant",
    //             },
    //             headers: { Authorization: token },
    //         });
    //         if (res?.success) {
    //             setMessage({ type: "success", text: "🎉 Redemption completed!" });
    //             setTimeout(() => { setDone(true); onRefresh?.(); }, 1500);
    //         } else {
    //             setMessage({ type: "warning", text: res?.message || "Approval failed" });
    //         }
    //     } catch (err) {
    //         setMessage({ type: "error", text: "Approval error" });
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleApprove = async () => {
        try {
            setLoading(true);
            const res = await fetchData({
                url: "/pd/streaks/redemption-confirm",
                method: "post",
                data: {
                    redemptionId: redemption._id,
                    confirmedBy: authState?.user?._id,
                    approvalRemarks: remarks.trim() || "Approved and redeemed by merchant",
                },
                headers: { Authorization: token },
            });
            if (res?.success) {
                setMessage({ type: "success", text: "🎉 Redemption completed!" });
                setTimeout(() => { setDone(true); onRefresh?.(); }, 1500);
            } else {
                setMessage({ type: "warning", text: res?.message || "Approval failed" });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Approval error" });
        } finally {
            setLoading(false);
        }
    };

    // ── Reject ──────────────────────────────────────────────────────
    // const handleReject = async () => {
    //     try {
    //         setLoading(true);
    //         const res = await fetchData({
    //             url: "/pd/streaks/redemption-reject",
    //             method: "post",
    //             data: {
    //                 redemptionId:    redemption._id,
    //                 rejectedBy:      authState?.user?._id,
    //                 rejectionRemarks: remarks.trim() || "Rejected by merchant",
    //             },
    //             headers: { Authorization: token },
    //         });
    //         if (res?.success) {
    //             setMessage({ type: "success", text: "❌ Redemption rejected" });
    //             setTimeout(() => { setDone(true); onRefresh?.(); }, 1200);
    //         } else {
    //             setMessage({ type: "warning", text: res?.message || "Rejection failed" });
    //         }
    //     } catch (err) {
    //         setMessage({ type: "error", text: "Rejection error" });
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // ── Confirm Final Redeem ─────────────────────────────────────────
    // const handleConfirmRedeem = async () => {
    //     try {
    //         setLoading(true);
    //         const res = await fetchData({
    //             url: "/pd/streaks/redemption-confirm",
    //             method: "post",
    //             data: {
    //                 redemptionId: redemption._id,
    //                 confirmedBy:  authState?.user?._id,
    //             },
    //             headers: { Authorization: token },
    //         });
    //         if (res?.success) {
    //             setMessage({ type: "success", text: "🎉 Redemption completed!" });
    //             setTimeout(() => { setDone(true); onRefresh?.(); }, 1500);
    //         } else {
    //             setMessage({ type: "warning", text: res?.message || "Confirm failed" });
    //         }
    //     } catch (err) {
    //         setMessage({ type: "error", text: "Confirm error" });
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // const isPending  = localStatus === "Pending";
    // const isApproved = localStatus === "Approved";
    const isPending = localStatus === "VoucherCreated";

    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
        <SwipeCard
            disabled={loading || !isPending}
            onSwipeRight={isPending ? handleApprove : undefined}
            // onSwipeLeft={isPending  ? handleReject  : undefined}
            rightLabel="✅ Approve"
            // leftLabel="❌ Reject"
        >
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-3">

                {/* Header */}
                <div className="flex justify-between items-start">
                    <div>
                        <div className="text-cyan-300 font-bold text-sm">
                            {redemption.userName}
                        </div>
                        <div className="text-[11px] text-gray-100">
                            {redemption.userCode}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-purple-300 font-black text-lg">
                            {redemption.requestedPoints}
                            <span className="text-xs font-normal text-gray-200 ml-1">pts</span>
                        </div>
                        <div className="text-[10px] text-gray-200">requested</div>
                    </div>
                </div>

                {/* Shop */}
                <div className="text-[11px] text-gray-100">
                    🏪 {redemption.shopName || redemption.shopkeeperName}
                    {" · "}{redemption.shopkeeperCode}
                </div>

                {/* Status pill */}
                <div className="flex justify-between items-center">
                    {/* <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${
                        isApproved
                            ? "bg-green-900/30 text-green-400 border-green-700/40"
                            : "bg-yellow-900/30 text-yellow-400 border-yellow-700/40"
                    }`}>
                        {isApproved ? "✅ Approved" : "⏳ Pending"}
                    </span> */}
                    <span className="text-[10px] text-gray-300">
                        {new Date(redemption.createdAt).toLocaleDateString("en-IN")}
                    </span>
                </div>

                {/* Feedback */}
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className={`p-2 rounded-lg text-xs font-bold text-center ${
                                message.type === "success"
                                    ? "bg-green-900/40 text-green-300 border border-green-700/40"
                                    : message.type === "warning"
                                    ? "bg-yellow-900/40 text-yellow-300 border border-yellow-700/40"
                                    : "bg-red-900/40 text-red-300 border border-red-700/40"
                            }`}
                        >
                            {message.text}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pending: fallback buttons */}
                {isPending && !message && (
                    <div className="space-y-2">
                        <div className="relative">
                            <div className="flex items-center gap-2">
                                <textarea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Add remarks.."
                                    style={{height: "50px"}}
                                    disabled={loading}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSuggestions((v) => !v)}
                                    disabled={loading}
                                    className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-800 border border-gray-700 text-sm hover:border-cyan-500/50 transition-colors"
                                    title="Suggested remarks"
                                >
                                    💡
                                </button>
                            </div>

                            {showSuggestions && (
                                <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                                    <div className="px-3 py-1.5 text-[10px] font-bold text-green-400 bg-green-900/20">✅ Approve Reasons</div>
                                    {REMARK_SUGGESTIONS.approve.map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => { setRemarks(r); setShowSuggestions(false); }}
                                            className="w-full text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-800 transition-colors"
                                        >
                                            {r}
                                        </button>
                                    ))}
                                    <div className="px-3 py-1.5 text-[10px] font-bold text-red-400 bg-red-900/20 border-t border-gray-800">❌ Reject Reasons</div>
                                    {REMARK_SUGGESTIONS.reject.map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => { setRemarks(r); setShowSuggestions(false); }}
                                            className="w-full text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-800 transition-colors"
                                        >
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handleApprove}
                                disabled={loading}
                                className="w-fit px-2 py-1 rounded-md bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-bold hover:bg-green-500/30 transition-colors"
                            >
                                ✅ Approve
                            </button>
                            {/* <button
                                onClick={handleReject}
                                disabled={loading}
                                className="w-fit px-2 py-1 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/30 transition-colors"
                            >
                                ❌ Reject
                            </button> */}
                        </div>
                    </div>
                )}

                {/* Approved: show final confirm button */}
                {/* {isApproved && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={handleConfirmRedeem}
                        disabled={loading}
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-black tracking-wide disabled:opacity-50"
                    >
                        {loading ? "Processing..." : "🎉 Confirm — Mark as Redeemed"}
                    </motion.button>
                )} */}
            </div>
        </SwipeCard>
        </div>
    );
};

export default RedemptionCard;
